/** @prettier */

/**
 * ゲートウェイ情報の編集
 */

import React, {useState, useEffect} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
//API import
import {
  putCompositionsGateway,
  getCompositionsGateway,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {gwInfoSort} from '../../functions/sort';
import {format} from '../../define';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';

const GWEditDialog = (props: {
  displayGWInfo: apiIF.resParameters.gatewayInformations;
  setGWInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsGateway | null>
  >;
  isOpenDialog: boolean;
  setIsOpenDialog: (arg0: boolean) => void;
}) => {
  //パスパラメータ取得
  const {siteID} = useParams();

  //編集前のゲートウェイの情報を取得
  const tempGWname =
    props.displayGWInfo['gateway-name'] === undefined
      ? ''
      : props.displayGWInfo['gateway-name'];
  const tempSystem = props.displayGWInfo['system'];
  let tempSLtype: '' | 'new' | 'old' = 'new';
  if (tempSystem === 'sl' && props.displayGWInfo['sl-type'] !== undefined) {
    tempSLtype = props.displayGWInfo['sl-type'];
  }

  const [gwName, setGWName] = useState(tempGWname); //ゲートウェイの名前
  const [gwSystem, setGWSystem] = useState(tempSystem); //系統番号
  const [slType, setSLType] = useState(tempSLtype); //SL新旧
  const [modbusMode, setModbusMode] = useState('active'); //Modbus有効無効（有効固定）
  const [alignment, setAlignment] = React.useState<string | null>(
    tempSLtype === 'old' ? 'right' : 'left'
  ); //SL新旧のトグルボタンの位置
  const [slTypeBtnDisabled, setSLTypeBtnDisabled] = useState(false); //SL新旧のトグルボタン非表示フラグ
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [gwNameError, setGWNameError] = useState(false); //ゲートウェイの名前

  const trans = useTranslation().t; //翻訳関数

  //入力項目のチェック
  useEffect(() => {
    if (gwName === '' || gwNameError || apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [gwName, gwNameError, apiWait]);

  /**
   * OKボタン押下時処理
   * @returns
   */
  const editGW = (gwID: string) => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }
    //修正項目の値が取得できない場合
    if (gwSystem === undefined || modbusMode === undefined) {
      return;
    }

    setAPIWait(true);

    const editData: apiIF.reqParameters.putCompositionsGateway = {
      'gateway-name': gwName,
      'system': gwSystem,
      'sl-type': slType,
    };
    //ゲートウェイの情報を修正
    putCompositionsGateway(siteID, gwID, editData)
      .then(() => {
        //ゲートウェイの情報を取得
        return getCompositionsGateway(siteID, {'max-result': 100});
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response = gwInfoSort(response);
        props.setGWInfo(response);
        props.setIsOpenDialog(false);
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
    setGWName(tempGWname);
    setGWSystem(tempSystem);
    setSLType(tempSLtype);
    setAlignment(tempSLtype === 'old' ? 'right' : 'left');
    setSLTypeBtnDisabled(tempSystem === 'sl' ? false : true);
  };

  /**
   * ゲートウェイの通信設定を変更するラジオボタン押下時処理
   * @param value 修正した通信設定
   */
  const eventSetEditGWType = (value: string) => {
    switch (value) {
      case 'sl':
        setGWSystem('sl');
        setSLType('new'); //new/oldをnewとする
        setSLTypeBtnDisabled(false); //new/oldの変更を有効とする
        break;
      case 'rt':
        setGWSystem('rt');
        setSLType('new'); //new/oldをnewとする
        setAlignment('left'); //new/old表示をnew固定とする
        setSLTypeBtnDisabled(true); //new/oldの変更を禁止する
        break;
      case 'modbus':
        // setGWSystem('MODBUS');
        setSLType('new'); //new/oldをnewとする
        setAlignment('left'); //new/old表示をnew固定とする
        setSLTypeBtnDisabled(true); //new/oldの変更を禁止する
        break;
    }
  };

  /**
   * SLタイプ変更時の処理
   * @param event
   * @param newAlignment
   */
  const handleAlignment = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    if (newAlignment !== null) {
      setAlignment(newAlignment);
    }
  };

  /**
   * SLタイプの変更するトグルボタン押下時処理
   * @param value 修正したSLタイプ
   */
  const eventSetEditSLType = (
    value: apiIF.reqParameters.putCompositionsGateway['sl-type']
  ) => {
    setSLType(value);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('GWEditDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={0}>
            {/* ゲートウェイの名前 */}
            <ChkInputTextbox
              setValue={setGWName}
              setError={setGWNameError}
              chkLength={format.GW_NAME_LENGTH_MAX}
              type="text"
              label={trans('GWEditDialog.txtField.label.gatewayName')}
              value={gwName}
              id="mv-inputEditGwName"
              class="gwSetting__contents__textBox--gwName"
              helperText=" "
              errHelperText={trans('GWEditDialog.txtField.errHelperTxt')}
              variant="standard"
              autoFocus={true}
              margin="dense"
              chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
            />
            {/* 通信設定 */}
            <RadioGroup
              defaultValue={gwSystem}
              aria-label="position"
              name="position">
              {/* 通信設定のラジオボタン（SL） */}
              <FormControlLabel
                value="sl"
                control={
                  <Radio color="primary" id={`mv-radioSL${tempGWname}`} />
                }
                label={trans('GWEditDialog.radioButtonLabel.superlink')}
                onChange={() => eventSetEditGWType('sl')}
              />
              {/* New/old設定のトグルボタン */}
              <ToggleButtonGroup
                value={alignment}
                exclusive
                onChange={handleAlignment}
                aria-label="text alignment">
                <ToggleButton
                  disabled={slTypeBtnDisabled}
                  value="left"
                  aria-label="left aligned"
                  onClick={() => eventSetEditSLType('new')}
                  id={`mv-btnSLNew${tempGWname}`}>
                  {trans('GWEditDialog.buttonTxt.new')}
                </ToggleButton>
                <ToggleButton
                  disabled={slTypeBtnDisabled}
                  value="right"
                  aria-label="right aligned"
                  onClick={() => eventSetEditSLType('old')}
                  id={`mv-btnSLOld${tempGWname}`}>
                  {trans('GWEditDialog.buttonTxt.old')}
                </ToggleButton>
              </ToggleButtonGroup>
              {/* 通信設定のラジオボタン（RT） */}
              <FormControlLabel
                value="rt"
                control={
                  <Radio color="primary" id={'mv-radioRT' + tempGWname} />
                }
                label={trans('GWEditDialog.radioButtonLabel.remoteTerminal')}
                onChange={() => eventSetEditGWType('rt')}
              />
            </RadioGroup>
          </Stack>
          {/* 室外機タイプを選択するラジオボタン */}
          {/* <FormControl>
            <FormLabel>Modbus tcp</FormLabel>
            <RadioGroup
              row
              defaultValue='invalid'
              aria-label="position"
              name="position">
              <FormControlLabel
                value="active"
                control={
                  <Radio
                    color="primary"
                    id={'mv-radioModbusEnable' + tempGWname}
                  />
                }
                label="Enable"
                onChange={() => setEditModbus('active')}
              />
              <FormControlLabel
                value="invalid"
                control={
                  <Radio
                    color="primary"
                    id={'mv-radioModbusDisenable' + tempGWname}
                  />
                }
                label="Invalid"
                onChange={() => setEditModbus('invalid')}
              />
            </RadioGroup>
          </FormControl> */}
        </DialogContent>
        <DialogActions>
          {/* OK/CANCELボタンの設定） */}
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('DeleteAccountDialog.buttonTxt.cancel')}
            id={`mv-btnCancelGwSettingEdit${tempGWname}`}
          />
          <PrimaryButton
            onClick={() => editGW(props.displayGWInfo['gateway-id'])}
            buttonText={trans('GWEditDialog.buttonTxt.ok')}
            id={`mv-btnOkGwSettingEdit${tempGWname}`}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`${tempGWname}EditErrDlgBtn`}
      />
    </>
  );
};
export default GWEditDialog;
